import { useEffect, useRef, useState } from "react";

function LazyImage({
  src,
  alt,
  className = "",
  wrapperClassName = "",
  skeletonClassName = "",
  ...props
}) {
  const wrapperRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) {
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: "0px 0px 200px 0px" }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    setHasLoaded(false);
  }, [src]);

  return (
    <div
      ref={wrapperRef}
      className={`tw:relative tw:overflow-hidden ${wrapperClassName}`.trim()}
    >
      {!hasLoaded && (
        <div
          aria-hidden="true"
          className={`tw:absolute tw:inset-0 tw:animate-pulse tw:bg-gray-200 ${skeletonClassName}`.trim()}
          style={{ borderRadius: "inherit" }}
        ></div>
      )}
      {shouldLoad && (
        <img
          src={src}
          alt={alt}
          className={`tw:w-full tw:h-full tw:object-cover tw:transition tw:duration-700 tw:ease-out ${
            hasLoaded
              ? "tw:opacity-100 tw:scale-100"
              : "tw:opacity-0 tw:scale-[1.02]"
          } ${className}`.trim()}
          // Let the browser decode off the main thread: these photos can be very
          // large, and a synchronous decode freezes scrolling while they appear.
          decoding="async"
          loading="lazy"
          onLoad={() => setHasLoaded(true)}
          onError={() => setHasLoaded(true)}
          {...props}
        />
      )}
    </div>
  );
}

export default LazyImage;
